import { baseUrl, ServiceType } from 'src/contstant/apiEndpoints';

/**
 * Typed frames emitted by the Starlight agent SSE stream (contract v1).
 * `reasoning` frames only arrive when extended thinking is enabled for the
 * stage; consumers must treat their absence as normal, not an error.
 */
export type AgentFrame =
  | { type: 'token'; text: string }
  | { type: 'reasoning'; text: string }
  | { type: 'tool'; status: 'started' | 'completed'; name: string; toolUseId: string }
  | { type: 'done' };

/**
 * Default inactivity window for the stream. If no frame (or keep-alive byte) arrives
 * within this window the stream is aborted. This is an *idle* timeout, not a total
 * cap: a healthy long turn keeps resetting it as tokens/tool frames arrive, so only a
 * genuinely stalled stream (dead connection, stuck downstream) trips it. The window
 * also covers connect + first byte, so a proxy that never starts the stream is caught.
 *
 * Sized above a normal turn (measured ~28s end-to-end) with headroom for a slow tool
 * call between frames, but well under the Starlight ALB idle timeout (240s) so the
 * client gives up — with a clear message — before the infra silently drops the socket.
 */
export const DEFAULT_STREAM_IDLE_TIMEOUT_MS = 60_000;

/** Raised when the stream is aborted because no data arrived within the idle window. */
export class StreamIdleTimeoutError extends Error {
  readonly idleTimeoutMs: number;
  constructor(idleTimeoutMs: number) {
    super(`Agent stream idle for ${idleTimeoutMs}ms`);
    this.name = 'StreamIdleTimeoutError';
    this.idleTimeoutMs = idleTimeoutMs;
  }
}

/**
 * Raised when the stream never started — the agent couldn't be reached or the proxy
 * couldn't open the downstream stream (network failure, or a non-OK initial response).
 * Distinct from a mid-stream drop (which keeps any partial answer) so the UI can tell the
 * user the assistant is unavailable rather than showing a generic error.
 */
export class StreamUnavailableError extends Error {
  readonly status?: number;
  constructor(status?: number) {
    super(
      status ? `Agent stream unavailable: HTTP ${status}` : 'Agent stream unavailable',
    );
    this.name = 'StreamUnavailableError';
    this.status = status;
  }
}

/**
 * Raised when the agent runtime reports a failure *inside* an otherwise healthy
 * stream. Starlight answers 200 and starts streaming, then — if the agent
 * crashes (e.g. a tool fails to load) — emits an untyped `{ error, error_type,
 * message }` frame and closes. Without this the turn would end looking like an
 * empty answer: no content, no error, nothing to act on.
 */
export class StreamErrorFrameError extends Error {
  /** The runtime's own diagnostic (for logs; never shown to the user). */
  readonly detail: string;
  readonly errorType?: string;
  constructor(message: string, detail: string, errorType?: string) {
    super(message);
    this.name = 'StreamErrorFrameError';
    this.detail = detail;
    if (errorType !== undefined) this.errorType = errorType;
  }
}

export interface StreamAgentParams {
  /** Logical agent selector resolved to a runtime by the Starlight target registry. */
  readonly agentId: string;
  readonly prompt: string;
  readonly businessUnitId: string;
  /** Stable per-conversation id (`^[A-Za-z0-9-_]+$`, 8–200 chars). */
  readonly sessionId: string;
  /** Invoked for every parsed frame, in stream order. */
  readonly onFrame: (frame: AgentFrame) => void;
  /** Optional caller-owned signal (e.g. cancel on unmount / new turn). Composed with the idle timeout. */
  readonly signal?: AbortSignal;
  /**
   * Inactivity window in ms before the stream is aborted. Reset on every received
   * chunk. Defaults to {@link DEFAULT_STREAM_IDLE_TIMEOUT_MS}.
   */
  readonly idleTimeoutMs?: number;
}

const isAgentFrame = (value: unknown): value is AgentFrame => {
  if (typeof value !== 'object' || value === null) return false;
  const { type } = value as { type?: unknown };
  return type === 'token' || type === 'reasoning' || type === 'tool' || type === 'done';
};

interface ErrorFrame {
  error: string;
  error_type?: string;
  message?: string;
}

/** The agent runtime's failure frame: untyped, identified by its `error` string. */
const isErrorFrame = (value: unknown): value is ErrorFrame => {
  if (typeof value !== 'object' || value === null) return false;
  const { error, type } = value as { error?: unknown; type?: unknown };
  return type === undefined && typeof error === 'string';
};

const toStreamErrorFrameError = (frame: ErrorFrame): StreamErrorFrameError =>
  new StreamErrorFrameError(
    frame.message ?? 'An error occurred during streaming',
    frame.error,
    frame.error_type,
  );

/**
 * Open the Starlight agent SSE stream (`POST /agent/{agentId}/stream`) and invoke
 * `onFrame` for each typed frame until the stream closes.
 *
 * Uses cookie auth (`credentials: 'include'`) to match the same-origin OIDC session
 * the app warms up, and `Accept: text/event-stream` to request the streaming surface.
 * Rejects if the initial response is not OK (e.g. the proxy could not start the
 * downstream stream); resolves once the body is fully consumed.
 */
export async function streamAgent({
  agentId,
  prompt,
  businessUnitId,
  sessionId,
  onFrame,
  signal,
  idleTimeoutMs = DEFAULT_STREAM_IDLE_TIMEOUT_MS,
}: StreamAgentParams): Promise<void> {
  const url = `${baseUrl(ServiceType.STARLIGHT)}/agent/${encodeURIComponent(agentId)}/stream`;

  // Compose our idle-timeout controller with the caller's optional signal: aborting
  // either aborts the fetch. A dedicated flag lets us tell an idle timeout apart from a
  // caller-initiated cancel so we can surface the right error.
  const controller = new AbortController();
  let idledOut = false;
  let idleTimer: ReturnType<typeof setTimeout> | undefined;

  const armIdleTimer = (): void => {
    if (idleTimer !== undefined) clearTimeout(idleTimer);
    idleTimer = setTimeout(() => {
      idledOut = true;
      controller.abort();
    }, idleTimeoutMs);
  };
  const clearIdleTimer = (): void => {
    if (idleTimer !== undefined) clearTimeout(idleTimer);
    idleTimer = undefined;
  };

  const onExternalAbort = (): void => controller.abort();
  if (signal) {
    if (signal.aborted) controller.abort();
    else signal.addEventListener('abort', onExternalAbort, { once: true });
  }

  // Tracks whether the stream produced any bytes: a failure before the first byte means
  // the agent was unreachable/couldn't start (unavailable); a failure after means a
  // mid-stream drop (which preserves any partial answer).
  let receivedAnyChunk = false;

  // Start the clock before fetch so connect + first byte is also bounded.
  armIdleTimer();

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'text/event-stream' },
      body: JSON.stringify({ prompt, businessUnitId, sessionId }),
      credentials: 'include',
      signal: controller.signal,
    });

    if (!response.ok || !response.body) {
      // The stream never started — surface as "unavailable" rather than a generic error.
      throw new StreamUnavailableError(response.status);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    // Set when the runtime reports a failure mid-stream; the stream is torn down and
    // the error thrown once the current chunk's lines are processed.
    let errorFrame: StreamErrorFrameError | undefined;

    const handleLine = (rawLine: string): void => {
      const line = rawLine.replace(/\r$/, '');
      if (!line.startsWith('data:')) return; // ignore comments / keep-alives / blank lines
      const payload = line.slice('data:'.length).trim();
      if (!payload) return;
      try {
        const parsed: unknown = JSON.parse(payload);
        if (isAgentFrame(parsed)) onFrame(parsed);
        else if (isErrorFrame(parsed)) errorFrame ??= toStreamErrorFrameError(parsed);
      } catch {
        // Non-JSON data line (e.g. keep-alive); ignore.
      }
    };

    // Buffer across chunks so a frame split mid-line is never truncated.
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      receivedAnyChunk = true;
      armIdleTimer(); // any inbound bytes (frame or keep-alive) count as activity
      buffer += decoder.decode(value, { stream: true });
      let newlineIndex = buffer.indexOf('\n');
      while (newlineIndex >= 0) {
        handleLine(buffer.slice(0, newlineIndex));
        buffer = buffer.slice(newlineIndex + 1);
        // Stop at the failure frame even if later lines arrived in the same chunk —
        // nothing after it may reach the consumer.
        if (errorFrame) break;
        newlineIndex = buffer.indexOf('\n');
      }
      if (errorFrame) {
        // Nothing useful follows a failure frame; release the connection and report it.
        // A rejected cancel (stream already broken) must not replace the typed error.
        await reader.cancel().catch(() => undefined);
        throw errorFrame;
      }
    }
    if (buffer) handleLine(buffer);
    if (errorFrame) throw errorFrame;
  } catch (err) {
    // An idle-triggered abort surfaces as an AbortError from fetch/read; translate it
    // to a typed, actionable error. Idle takes precedence over the other classifications.
    if (idledOut) throw new StreamIdleTimeoutError(idleTimeoutMs);
    // Typed failures already carry their classification — pass them through.
    if (err instanceof StreamUnavailableError || err instanceof StreamErrorFrameError) {
      throw err;
    }
    // A failure before any byte arrived (e.g. fetch rejected: network/DNS/CORS) means the
    // agent was unreachable. A failure after bytes arrived is a genuine mid-stream drop.
    if (!receivedAnyChunk) throw new StreamUnavailableError();
    throw err;
  } finally {
    clearIdleTimer();
    if (signal) signal.removeEventListener('abort', onExternalAbort);
  }
}
